import { useEffect, useMemo, useState } from "react";
import { useGT } from "gt-react";
import { Database, Table2, Braces, KeyRound, ChevronRight } from "lucide-react";

interface CatalogBrowserProps {
  catalogKind: "mongo-catalog" | "postgres-catalog";
  catalogJson: string;
  filter: string;
}

interface MongoCatalogIndex {
  name?: string;
  key?: unknown;
  [key: string]: unknown;
}

interface MongoCatalogCollection {
  name: string;
  type: string;
  options?: Record<string, unknown>;
  indexes: MongoCatalogIndex[];
  fieldPaths: string[];
}

interface MongoCatalog {
  kind: "mongo-catalog";
  database: string;
  collections: MongoCatalogCollection[];
}

interface PostgresCatalogIndex {
  name: string;
  definition: string;
}

interface PostgresCatalogColumn {
  name: string;
  dataType: string;
  nullable: boolean;
}

interface PostgresCatalogTable {
  schema: string;
  name: string;
  type: string;
  columns: PostgresCatalogColumn[];
  indexes: PostgresCatalogIndex[];
}

interface PostgresCatalogSchema {
  name: string;
  tables: PostgresCatalogTable[];
}

interface PostgresCatalog {
  kind: "postgres-catalog";
  schemas: PostgresCatalogSchema[];
}

function safeJsonParse<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function includesNeedle(value: string, needle: string): boolean {
  return value.toLowerCase().includes(needle);
}

function stringifyIndexKey(value: unknown): string {
  if (value == null) return "";
  return JSON.stringify(value) ?? String(value);
}

function filterMongoCatalog(catalog: MongoCatalog, filter: string): MongoCatalog {
  const needle = filter.trim().toLowerCase();
  if (!needle) return catalog;

  return {
    ...catalog,
    collections: catalog.collections.filter((collection) => {
      if (includesNeedle(collection.name, needle)) return true;
      if (collection.fieldPaths.some((fieldPath) => includesNeedle(fieldPath, needle))) return true;
      if (collection.indexes.some((index) =>
        includesNeedle(index.name ?? "", needle) || includesNeedle(stringifyIndexKey(index.key), needle)
      )) return true;
      return false;
    }),
  };
}

function filterPostgresCatalog(catalog: PostgresCatalog, filter: string): PostgresCatalog {
  const needle = filter.trim().toLowerCase();
  if (!needle) return catalog;

  return {
    ...catalog,
    schemas: catalog.schemas
      .map((schema) => ({
        ...schema,
        tables: schema.tables.filter((table) => {
          if (includesNeedle(schema.name, needle) || includesNeedle(table.name, needle)) return true;
          if (table.columns.some((column) =>
            includesNeedle(column.name, needle) || includesNeedle(column.dataType, needle)
          )) return true;
          if (table.indexes.some((index) =>
            includesNeedle(index.name, needle) || includesNeedle(index.definition, needle)
          )) return true;
          return false;
        }),
      }))
      .filter((schema) => schema.tables.length > 0),
  };
}

function collectionKey(collection: MongoCatalogCollection): string {
  return collection.name;
}

function tableKey(table: PostgresCatalogTable): string {
  return `${table.schema}.${table.name}`;
}

function CatalogEmptyState({ title }: { title: string }) {
  return (
    <div className="flex h-full items-center justify-center text-sm text-zinc-600">
      {title}
    </div>
  );
}

// --- Field Path Tree (Mongo) ---

interface FieldTreeNode {
  name: string;
  fullPath: string;
  isArray: boolean;
  children: FieldTreeNode[];
}

function buildFieldTree(fieldPaths: string[]): FieldTreeNode[] {
  interface BuildNode { name: string; fullPath: string; isArray: boolean; children: Map<string, BuildNode> }
  const root = new Map<string, BuildNode>();

  for (const raw of fieldPaths) {
    const path = raw.startsWith("[].") ? raw.slice(3) : raw;
    if (!path || path === "[]") continue;

    const parts = path.split(".");
    const segments: { name: string; isArray: boolean }[] = [];
    for (const part of parts) {
      if (part === "[]") {
        if (segments.length > 0) segments[segments.length - 1]!.isArray = true;
      } else if (part.endsWith("[]")) {
        segments.push({ name: part.slice(0, -2), isArray: true });
      } else {
        segments.push({ name: part, isArray: false });
      }
    }

    let level = root;
    let fp = "";
    for (const seg of segments) {
      fp = fp ? `${fp}.${seg.name}` : seg.name;
      let node = level.get(seg.name);
      if (!node) {
        node = { name: seg.name, fullPath: fp, isArray: seg.isArray, children: new Map() };
        level.set(seg.name, node);
      }
      if (seg.isArray) node.isArray = true;
      level = node.children;
    }
  }

  function toArray(map: Map<string, BuildNode>): FieldTreeNode[] {
    return [...map.values()]
      .map((n) => ({ name: n.name, fullPath: n.fullPath, isArray: n.isArray, children: toArray(n.children) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
  return toArray(root);
}

function FieldTreeRow({ node, depth, expanded, onToggle }: {
  node: FieldTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (path: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.fullPath);

  return (
    <>
      <div
        className={`flex items-center gap-1.5 rounded py-[3px] pr-2 ${hasChildren ? "cursor-pointer hover:bg-surface-overlay/30" : ""}`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={hasChildren ? () => onToggle(node.fullPath) : undefined}
      >
        {hasChildren ? (
          <ChevronRight size={10} className={`shrink-0 text-zinc-600 transition-transform ${isOpen ? "rotate-90" : ""}`} />
        ) : (
          <span className="w-[10px] shrink-0" />
        )}
        <span className={`font-mono text-[11px] ${hasChildren ? "text-zinc-200" : "text-zinc-400"}`}>
          {node.name}
        </span>
        {node.isArray && <span className="font-mono text-[10px] text-brand/60">[]</span>}
        {hasChildren && !isOpen && (
          <span className="ml-0.5 text-[10px] text-zinc-600">{node.children.length}</span>
        )}
      </div>
      {hasChildren && isOpen && node.children.map((child) => (
        <FieldTreeRow key={child.fullPath} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} />
      ))}
    </>
  );
}

function FieldPathTree({ fieldPaths }: { fieldPaths: string[] }) {
  const tree = useMemo(() => buildFieldTree(fieldPaths), [fieldPaths]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const toggle = (path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  if (tree.length === 0) return null;

  return (
    <div className="py-1">
      {tree.map((node) => (
        <FieldTreeRow key={node.fullPath} node={node} depth={0} expanded={expanded} onToggle={toggle} />
      ))}
    </div>
  );
}

function MongoCatalogView({ catalog, filter }: { catalog: MongoCatalog; filter: string }) {
  const t = useGT();
  const filtered = useMemo(() => filterMongoCatalog(catalog, filter), [catalog, filter]);
  const [selectedCollectionName, setSelectedCollectionName] = useState<string | null>(filtered.collections[0]?.name ?? null);

  useEffect(() => {
    const nextSelection = filtered.collections.find((collection) => collection.name === selectedCollectionName)?.name
      ?? filtered.collections[0]?.name
      ?? null;
    if (nextSelection !== selectedCollectionName) {
      setSelectedCollectionName(nextSelection);
    }
  }, [filtered.collections, selectedCollectionName]);

  const selectedCollection = filtered.collections.find((collection) => collection.name === selectedCollectionName) ?? null;

  if (filtered.collections.length === 0) {
    return <CatalogEmptyState title={t("No collections match the current filter")} />;
  }

  return (
    <div className="grid min-h-0 flex-1 grid-cols-[280px_minmax(0,1fr)]">
      <div className="min-h-0 overflow-auto border-r border-border bg-surface-raised/40">
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
            <Database size={12} />
            {catalog.database}
          </div>
          <div className="mt-1 text-xs text-zinc-600">
            {filtered.collections.length} {t("collections")}
          </div>
        </div>
        <div className="p-2">
          {filtered.collections.map((collection) => {
            const selected = collection.name === selectedCollectionName;
            return (
              <button
                key={collectionKey(collection)}
                type="button"
                onClick={() => setSelectedCollectionName(collection.name)}
                className={`mb-1 w-full rounded-md border px-3 py-2 text-left transition ${
                  selected
                    ? "border-brand bg-brand/10 text-zinc-100"
                    : "border-transparent bg-transparent text-zinc-400 hover:border-border hover:bg-surface hover:text-zinc-200"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{collection.name}</span>
                  <span className="rounded bg-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-500">
                    {collection.type}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-zinc-500">
                  {collection.fieldPaths.length} {t("fields")} · {collection.indexes.length} {t("indexes")}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 overflow-auto">
        {selectedCollection ? (
          <div className="mx-auto flex max-w-5xl flex-col gap-5 px-5 py-5">
            <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{t("Collection")}</div>
                <div className="mt-1 text-xl font-semibold text-zinc-100">{selectedCollection.name}</div>
                <div className="mt-2 text-xs text-zinc-500">
                  {selectedCollection.fieldPaths.length} {t("field paths")} · {selectedCollection.indexes.length} {t("indexes")}
                </div>
              </div>
              <div className="rounded-full border border-border bg-surface-raised px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                {selectedCollection.type}
              </div>
            </div>

            <section className="rounded-lg border border-border bg-surface-raised/30">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                <Braces size={12} />
                {t("Fields")}
              </div>
              <FieldPathTree fieldPaths={selectedCollection.fieldPaths} />
            </section>

            <section className="rounded-lg border border-border bg-surface-raised/30">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                <KeyRound size={12} />
                {t("Indexes")}
              </div>
              <div className="divide-y divide-border">
                {selectedCollection.indexes.map((index, indexPosition) => (
                  <div key={`${index.name ?? "index"}-${indexPosition}`} className="px-4 py-3">
                    <div className="text-sm font-medium text-zinc-200">{index.name ?? t("Unnamed index")}</div>
                    <pre className="mt-2 overflow-auto whitespace-pre-wrap rounded bg-surface px-3 py-2 font-mono text-[11px] text-zinc-400">
                      {JSON.stringify(index, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PostgresCatalogView({ catalog, filter }: { catalog: PostgresCatalog; filter: string }) {
  const t = useGT();
  const filtered = useMemo(() => filterPostgresCatalog(catalog, filter), [catalog, filter]);
  const tables = useMemo(() => filtered.schemas.flatMap((schema) => schema.tables), [filtered.schemas]);
  const [selectedTableName, setSelectedTableName] = useState<string | null>(tables[0] ? tableKey(tables[0]) : null);

  useEffect(() => {
    const nextSelection = tables.find((table) => tableKey(table) === selectedTableName)
      ? selectedTableName
      : tables[0]
        ? tableKey(tables[0])
        : null;
    if (nextSelection !== selectedTableName) {
      setSelectedTableName(nextSelection);
    }
  }, [selectedTableName, tables]);

  const selectedTable = tables.find((table) => tableKey(table) === selectedTableName) ?? null;

  if (tables.length === 0) {
    return <CatalogEmptyState title={t("No tables match the current filter")} />;
  }

  return (
    <div className="grid min-h-0 flex-1 grid-cols-[300px_minmax(0,1fr)]">
      <div className="min-h-0 overflow-auto border-r border-border bg-surface-raised/40">
        <div className="border-b border-border px-4 py-3 text-xs text-zinc-500">
          <div className="flex items-center gap-2 font-semibold uppercase tracking-[0.18em]">
            <Database size={12} />
            {filtered.schemas.length} {t("schemas")}
          </div>
          <div className="mt-1 text-zinc-600">{tables.length} {t("tables")}</div>
        </div>
        <div className="p-2">
          {filtered.schemas.map((schema) => (
            <div key={schema.name} className="mb-3">
              <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-600">
                {schema.name}
              </div>
              {schema.tables.map((table) => {
                const selected = tableKey(table) === selectedTableName;
                return (
                  <button
                    key={tableKey(table)}
                    type="button"
                    onClick={() => setSelectedTableName(tableKey(table))}
                    className={`mb-1 w-full rounded-md border px-3 py-2 text-left transition ${
                      selected
                        ? "border-brand bg-brand/10 text-zinc-100"
                        : "border-transparent bg-transparent text-zinc-400 hover:border-border hover:bg-surface hover:text-zinc-200"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium">{table.name}</span>
                      <span className="rounded bg-surface px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-500">
                        {table.type === "VIEW" ? "view" : "table"}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-zinc-500">
                      {table.columns.length} {t("columns")} · {table.indexes.length} {t("indexes")}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="min-h-0 overflow-auto">
        {selectedTable ? (
          <div className="mx-auto flex max-w-6xl flex-col gap-5 px-5 py-5">
            <div className="flex items-start justify-between gap-4 border-b border-border pb-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{t("Table")}</div>
                <div className="mt-1 flex items-center gap-2 text-xl font-semibold text-zinc-100">
                  <span>{selectedTable.schema}.{selectedTable.name}</span>
                </div>
                <div className="mt-2 text-xs text-zinc-500">
                  {selectedTable.columns.length} {t("columns")} · {selectedTable.indexes.length} {t("indexes")}
                </div>
              </div>
              <div className="rounded-full border border-border bg-surface-raised px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                {selectedTable.type}
              </div>
            </div>

            <section className="rounded-lg border border-border bg-surface-raised/30">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                <Table2 size={12} />
                {t("Columns")}
              </div>
              <div className="overflow-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface/70 text-left text-[11px] uppercase tracking-[0.18em] text-zinc-600">
                      <th className="px-4 py-2 font-semibold">{t("Name")}</th>
                      <th className="px-4 py-2 font-semibold">{t("Type")}</th>
                      <th className="px-4 py-2 font-semibold">{t("Nullable")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTable.columns.map((column) => (
                      <tr key={column.name} className="border-b border-border/70 last:border-b-0">
                        <td className="px-4 py-2 font-mono text-[12px] text-zinc-200">{column.name}</td>
                        <td className="px-4 py-2 font-mono text-[12px] text-zinc-400">{column.dataType}</td>
                        <td className="px-4 py-2 text-zinc-400">{column.nullable ? t("yes") : t("no")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="rounded-lg border border-border bg-surface-raised/30">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                <KeyRound size={12} />
                {t("Indexes")}
              </div>
              <div className="divide-y divide-border">
                {selectedTable.indexes.length > 0 ? selectedTable.indexes.map((index) => (
                  <div key={index.name} className="px-4 py-3">
                    <div className="text-sm font-medium text-zinc-200">{index.name}</div>
                    <pre className="mt-2 overflow-auto whitespace-pre-wrap rounded bg-surface px-3 py-2 font-mono text-[11px] text-zinc-400">
                      {index.definition}
                    </pre>
                  </div>
                )) : (
                  <div className="px-4 py-4 text-sm text-zinc-500">{t("No indexes recorded for this table")}</div>
                )}
              </div>
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function CatalogBrowser({ catalogKind, catalogJson, filter }: CatalogBrowserProps) {
  const t = useGT();

  if (catalogKind === "mongo-catalog") {
    const catalog = safeJsonParse<MongoCatalog>(catalogJson);
    if (!catalog) return <CatalogEmptyState title={t("Catalog JSON is invalid")} />;
    return <MongoCatalogView catalog={catalog} filter={filter} />;
  }

  const catalog = safeJsonParse<PostgresCatalog>(catalogJson);
  if (!catalog) return <CatalogEmptyState title={t("Catalog JSON is invalid")} />;
  return <PostgresCatalogView catalog={catalog} filter={filter} />;
}
